import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { WEEK_CALENDAR_DAY_INDEX_ATTR } from '../constants';
import { getDayStep, isAllDaysFit } from '../utils';
import { WeekCalendarSliderMode } from '../types';

export const useScrollCalendar = ({
	mode = 'scroll',
	calendarDaysCount,
	selectedIndex,
	dates,
	centralDate,
	loading,
	disabledLeftArrow,
	disabledRightArrow,
	minItemWidth = 100,
	onRangeChange
}: {
	mode?: WeekCalendarSliderMode;
	calendarDaysCount?: number;
	selectedIndex: number;
	dates: Date[];
	centralDate?: Date;
	loading?: boolean;
	disabledLeftArrow?: boolean;
	disabledRightArrow?: boolean;
	minItemWidth?: number;
	onRangeChange?: (type: 'increase' | 'decrease', visibleDates: number) => void;
}) => {
	const isStatic = mode === 'static';
	const datesRangeKey = dates[0]?.getTime() ?? 0;
	const centralDateTimestamp = centralDate?.getTime() ?? 0;

	const viewportRef = useRef<HTMLDivElement>(null);
	const realScrollWidthRef = useRef(0);
	const allDaysFitRef = useRef(false);

	const [allDaysFit, setAllDaysFit] = useState(false);
	const [visibleDates, setVisibleDates] = useState(1);

	const touchStartXRef = useRef(0);
	const touchTotalDeltaRef = useRef(0);
	const touchActiveRef = useRef(false);

	/** Пересчитывает, сколько дней видно во viewport, и помещаются ли все дни без скролла */
	const updateViewportGeometry = useCallback(() => {
		if (isStatic) {
			allDaysFitRef.current = true;
			setAllDaysFit(true);
			setVisibleDates(calendarDaysCount ?? dates.length);

			return;
		}

		const viewport = viewportRef.current;

		if (!viewport) {
			return;
		}

		const daysContainer = viewport.firstElementChild as HTMLElement | null;

		if (!daysContainer) {
			return;
		}

		if (!allDaysFitRef.current) {
			realScrollWidthRef.current = daysContainer.scrollWidth;
		}

		const fits = isAllDaysFit(viewport, realScrollWidthRef.current);
		const step = getDayStep(daysContainer);

		if (step > 0) {
			const itemsCount = daysContainer.children.length;

			setVisibleDates(fits && itemsCount > 0 ? itemsCount : Math.max(1, Math.floor(viewport.clientWidth / step)));
		}

		allDaysFitRef.current = fits;
		setAllDaysFit(fits);
	}, [isStatic, calendarDaysCount, dates.length]);

	/** Прокручивает viewport так, чтобы выбранный день оказался по центру (или сбрасывает scroll, если все дни помещаются) */
	const scrollToSelected = useCallback(() => {
		const viewport = viewportRef.current;

		if (!viewport) {
			return;
		}

		const daysContainer = viewport.firstElementChild;

		const selectedEl = daysContainer?.querySelector(
			`[${WEEK_CALENDAR_DAY_INDEX_ATTR}="${selectedIndex}"]`
		) as HTMLElement | null;

		if (!selectedEl || !daysContainer) {
			return;
		}

		if (allDaysFit) {
			viewport.scrollLeft = 0;

			return;
		}

		const maxScroll = Math.max(0, daysContainer.scrollWidth - viewport.clientWidth);

		if (maxScroll <= 0) {
			viewport.scrollLeft = 0;

			return;
		}

		const itemLeft = selectedEl.offsetLeft;
		const target = itemLeft - (viewport.clientWidth - selectedEl.offsetWidth) / 2;

		viewport.scrollLeft = Math.max(0, Math.min(target, maxScroll));
	}, [selectedIndex, allDaysFit]);

	/** Сброс геометрии при смене диапазона дат, выбранного дня или состояния загрузки */
	useLayoutEffect(() => {
		realScrollWidthRef.current = 0;
		allDaysFitRef.current = false;
		setAllDaysFit(false);
		updateViewportGeometry();
	}, [selectedIndex, dates.length, datesRangeKey, centralDateTimestamp, loading, updateViewportGeometry]);

	useLayoutEffect(() => {
		if (isStatic) {
			return;
		}

		scrollToSelected();
	}, [isStatic, selectedIndex, allDaysFit, scrollToSelected, datesRangeKey]);

	/** Пересчёт при изменении размера viewport; scrollToSelected — только если режим «все дни помещаются» не изменился */
	useLayoutEffect(() => {
		if (isStatic) {
			return undefined;
		}

		const viewport = viewportRef.current;

		if (!viewport) {
			return undefined;
		}

		const daysContainer = viewport.firstElementChild;

		if (!daysContainer) {
			return undefined;
		}

		const observer = new ResizeObserver(() => {
			const prevFits = allDaysFitRef.current;

			updateViewportGeometry();

			if (prevFits === allDaysFitRef.current) {
				scrollToSelected();
			}
		});

		observer.observe(viewport);
		observer.observe(daysContainer);

		return () => observer.disconnect();
	}, [isStatic, updateViewportGeometry, scrollToSelected]);

	const navigate = useCallback(
		(direction: 'prev' | 'next') => {
			if (!onRangeChange || visibleDates === 0 || dates.length === 0) {
				return;
			}

			if (direction === 'prev' && disabledLeftArrow) {
				return;
			}

			if (direction === 'next' && disabledRightArrow) {
				return;
			}

			onRangeChange(direction === 'prev' ? 'decrease' : 'increase', visibleDates);
		},
		[onRangeChange, visibleDates, disabledLeftArrow, disabledRightArrow, dates.length]
	);

	const onTouchStart = (event: React.TouchEvent) => {
		touchActiveRef.current = true;
		touchStartXRef.current = event.touches[0].clientX;
		touchTotalDeltaRef.current = 0;
	};

	const onTouchMove = (event: React.TouchEvent) => {
		if (!touchActiveRef.current) {
			return;
		}

		const delta = event.touches[0].clientX - touchStartXRef.current;

		touchTotalDeltaRef.current += delta;
		touchStartXRef.current = event.touches[0].clientX;
	};

	const onTouchEnd = useCallback(() => {
		touchActiveRef.current = false;

		const threshold = minItemWidth / 1.5;

		if (Math.abs(touchTotalDeltaRef.current) > threshold) {
			navigate(touchTotalDeltaRef.current > 0 ? 'prev' : 'next');
		}
		touchTotalDeltaRef.current = 0;
	}, [navigate, minItemWidth]);

	return {
		viewportRef,
		allDaysFit: isStatic ? true : allDaysFit,
		navigate,
		onTouchStart: isStatic ? undefined : onTouchStart,
		onTouchMove: isStatic ? undefined : onTouchMove,
		onTouchEnd: isStatic ? undefined : onTouchEnd
	};
};
